import React from 'react';
import { motion } from 'framer-motion';
import { cvData } from '../data/cvData';
import { uiTranslations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import SectionHeader from './SectionHeader';
import SafeIcon from '../common/SafeIcon';

const DocCard = ({ doc, type, index }) => (
  <motion.a
    href={doc.url}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="group bg-white p-6 rounded-xl border border-slate-200 hover:border-accent hover:shadow-xl hover:shadow-accent/5 transition-all duration-300 flex flex-col h-full"
  >
    <div className="flex items-start justify-between mb-6">
      <div className="p-3 bg-slate-50 text-slate-400 group-hover:bg-accent/10 group-hover:text-accent rounded-lg transition-colors">
        <SafeIcon name={type === 'cert' ? 'Award' : (type === 'app' ? 'User' : 'FileText')} className="w-6 h-6" />
      </div>
      <SafeIcon name="ExternalLink" className="w-4 h-4 text-slate-300 group-hover:text-accent transition-colors" />
    </div>
    <h4 className="text-slate-900 font-bold mb-2 group-hover:text-accent transition-colors flex items-center flex-wrap gap-2">
      {doc.title}
      {doc.lang === 'de' && (
        <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full inline-flex items-center gap-1 border border-slate-200">
          🇩🇪 DE
        </span>
      )}
      {doc.lang === 'en' && (
        <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full inline-flex items-center gap-1 border border-slate-200">
          🇬🇧 EN
        </span>
      )}
    </h4>
    <p className="text-sm text-slate-500 mb-4 flex-grow">
      {doc.issuer || doc.type}
    </p>
    {doc.quote && (
      <blockquote className="text-xs italic text-slate-400 border-l-2 border-slate-100 pl-3 mb-4 line-clamp-2">
        "{doc.quote}"
      </blockquote>
    )}
    {doc.date && (
      <div className="text-xs font-bold text-slate-400 tracking-wider uppercase mt-auto pt-4 border-t border-slate-50">
        {doc.date}
      </div>
    )}
  </motion.a>
);

export default function Documents() {
  const { language } = useLanguage();
  const data = cvData[language];
  const t = uiTranslations[language].sections.documents;
  const labels = uiTranslations[language].docs;

  const { certificates, references, application } = data.documents;

  const hasApp = application && application.length > 0;
  const hasRef = references && references.length > 0;
  const hasCert = certificates && certificates.length > 0;

  if (!hasApp && !hasRef && !hasCert) return null;

  return (
    <section id="documents" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <SectionHeader title={t.title} label={t.label} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          {hasApp && (
            <div className="flex flex-col gap-8">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                <span className="w-6 h-1 bg-accent rounded-full"></span>
                {labels.application}
              </h3>
              <div className="flex flex-col gap-6">
                {application.map((doc, i) => (
                  <DocCard key={doc.id} doc={doc} type="app" index={i} />
                ))}
              </div>
            </div>
          )}

          {hasRef && (
            <div className="flex flex-col gap-8">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                <span className="w-6 h-1 bg-accent rounded-full"></span>
                {labels.references}
              </h3>
              <div className="flex flex-col gap-6">
                {references.map((doc, i) => (
                  <DocCard key={doc.id} doc={doc} type="ref" index={i} />
                ))}
              </div>
            </div>
          )}

          {hasCert && (
            <div className="flex flex-col gap-8">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                <span className="w-6 h-1 bg-accent rounded-full"></span>
                {labels.certificates}
              </h3>
              <div className="flex flex-col gap-6">
                {certificates.map((doc, i) => (
                  <DocCard key={doc.id} doc={doc} type="cert" index={i} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}